import type { ApiErrorBody } from '@clair-avenir/shared';

export class ApiError extends Error {
  constructor(public readonly body: ApiErrorBody) {
    super(body.message);
    this.name = 'ApiError';
  }
  get status() {
    return this.body.statusCode;
  }
  get code() {
    return this.body.code;
  }
  /** Erros de campo (422) indexados por path. */
  get fieldErrors(): Record<string, string> {
    return Object.fromEntries((this.body.details ?? []).map((d) => [d.path, d.message]));
  }
}

const BASE = '/api/v1';

interface HttpOptions extends RequestInit {
  json?: unknown;
  /** Não tenta renovar a sessão automaticamente em caso de 401 (usado pelo próprio refresh/login/logout). */
  skipAuthRefresh?: boolean;
  /** Uso interno: marca que esta chamada já é uma retentativa pós-refresh. */
  _retried?: boolean;
}

async function doFetch<T>(path: string, init: HttpOptions): Promise<{ res: Response; data: T | undefined }> {
  const { json, headers, ...rest } = init;
  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      credentials: 'include',
      headers: { Accept: 'application/json', ...(json !== undefined ? { 'Content-Type': 'application/json' } : {}), ...headers },
      body: json !== undefined ? JSON.stringify(json) : rest.body,
      ...rest,
    });
  } catch {
    throw new ApiError({ statusCode: 0, code: 'NETWORK', message: 'Sem conexão. Verifique sua internet e tente de novo.' });
  }
  if (res.status === 204) return { res, data: undefined };
  const text = await res.text();
  const data = text ? (JSON.parse(text) as T) : undefined;
  return { res, data };
}

let refreshInFlight: Promise<boolean> | null = null;

/** Coalesce várias renovações simultâneas (várias queries 401 ao mesmo tempo) numa só chamada. */
function refreshSession(): Promise<boolean> {
  refreshInFlight ??= doFetch('/auth/refresh', { method: 'POST', skipAuthRefresh: true })
    .then(({ res }) => res.ok)
    .catch(() => false)
    .finally(() => {
      refreshInFlight = null;
    });
  return refreshInFlight;
}

export async function http<T>(path: string, init: HttpOptions = {}): Promise<T> {
  const { res, data } = await doFetch<T>(path, init);
  if (res.ok) return data as T;

  if (res.status === 401 && !init.skipAuthRefresh && !init._retried) {
    const renewed = await refreshSession();
    if (renewed) return http<T>(path, { ...init, _retried: true });
  }

  const body = (data as ApiErrorBody | undefined) ?? { statusCode: res.status, code: `HTTP_${res.status}`, message: 'Algo deu errado.' };
  throw new ApiError(body);
}
